import { ReactComponent as MagnifyingGlass } from "assets/svg/magnifying-glass.svg";
import { ReactComponent as BackIcon } from "assets/svg/outline-arrow-left.svg";
import { ReactComponent as EditBtn } from "assets/svg/outline-edit-16.svg";
import { ReactComponent as OutlinePlus16 } from "assets/svg/outline-plus-16.svg";
import { ReactComponent as ProfileIcon } from "assets/svg/user-profile.svg";
import Loading from "components/Loading";
import IconButton from "components/icon-button";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";
import { useDebouncedCallback } from "hooks/useDebouncedCallback";
import { useState } from "react";
import { Button, Form, Table } from "react-bootstrap";
import MoreActionsDropdown from "../../utils/MoreActionsDropdown";
import data from "../../utils/teams.json";
import styles from "./Permission.module.scss";

const { PermissionsForUser, client_chart } = data;

const tabItems = ["Personal Info", "Permissions", "Service and Availability"];

const Permissions = ({ setCurrentPage }) => {
  const [selectedTab, setSelectedTab] =
    useState<typeof tabItems[number]>("Permissions");
  const [selectedClients, setSelectedClients] = useState<number[]>([]);
  const [filteredClients, setFilteredClients] = useState(client_chart);
  const { currentMember } = useTeamContext();
  

  const getFullName = () => {
    return currentMember
      ? `${currentMember.first_name} ${currentMember.last_name}`
      : "";
  };

  const handleTabSelect = (tab: typeof tabItems[number]) => {
    setSelectedTab(tab);
  };

  const handleClientSelect = (id: number) => {
    if (selectedClients.includes(id)) {
      const newClients = selectedClients.filter((clientId) => clientId !== id);
      setSelectedClients(newClients);
    } else {
      setSelectedClients([...selectedClients, id]);
    }
  };
  const handleAllClientSelect = (selectAll: boolean) => {
    if (selectAll) {
      const allClientIds = client_chart.map((client) => client.id);
      setSelectedClients(allClientIds);
    } else {
      setSelectedClients([]);
    }
  };

  const debouncedFilter = useDebouncedCallback((value) => {
    if (!client_chart) return;

    const newClients = client_chart.filter((client) =>
      client.name.toLowerCase().includes(value.toLowerCase()),
    );
    setFilteredClients(newClients);
  }, 500);

  const getDropdownActions = () => {
    return [
      {
        label: "More",
        actionLabel: "MORE_OPTIONS",
        onClick: () => {
          console.log("More Options clicked");
        },
        className: ``,
      },
    ];
  };
  if (!currentMember) return <Loading />;

  return (
    <div className={styles["permission__container"]}>
      <div className={styles["permission__header"]}>
        <span className={styles["permission__header--personal-info"]}>
          <span>
            <BackIcon
              className={styles["permission__header--back-icon"]}
              onClick={() => setCurrentPage("members")}
            />
          </span>
          <span>
            <ProfileIcon
              className={styles["permission__header--profile-icon"]}
            />
          </span>
          <span className={styles["permission__header--name"]}>
            {getFullName()}
          </span>
        </span>
        <span className={styles["permission__header--status"]}>Active</span>
      </div>
      {/* personal info */}
      <div className="pt-2 pb-2">
        <span
          className={`${styles["permission__nav-item"]} ${selectedTab === "Personal Info" &&
            styles["permission__nav-item--selected"]
            }`}
          onClick={() => handleTabSelect("Personal Info")}
        >
          Personal Info
        </span>
        <span
          className={`${styles["permission__nav-item"]} ${selectedTab === "Permissions" &&
            styles["permission__nav-item--selected"]
            }`}
          onClick={() => handleTabSelect("Permissions")}
        >
          Permissions
        </span>
        <span
          className={`${styles["permission__nav-item"]} ${selectedTab === "Service and Availability" &&
            styles["permission__nav-item--selected"]
            }`}
          onClick={() => handleTabSelect("Service and Availability")}
        >
          Service and Availability
        </span>
      </div>
      {/*tabs */}
      <div className={styles["permission__tables"]}>
        <div className={styles["permission__permission-table-container"]}>
          <div className="d-flex align-items-center pt-2 pb-2">
            <div className={`${styles["permission__nav-item--selected"]} mr-3`}>
              {selectedTab}
            </div>
            <MoreActionsDropdown actions={getDropdownActions()} member={null} />
            <IconButton>
              <EditBtn />
            </IconButton>
          </div>
          <div className={styles["permission__permissions-chart"]}>
            <Table>
              <thead className={styles["permission__table-header"]}>
                <tr>
                  <th>Permission</th>
                  <th>View Access</th>
                  <th>Edit Access</th>
                </tr>
              </thead>
              <tbody>
                {PermissionsForUser.map((permission) => {
                  return (
                    <tr key={permission.id}>
                      <td>{permission.permission}</td>
                      <td>{permission.viewAccess}</td>
                      <td>{permission.editAccess}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
        <div className={styles["permission__client-table-container"]}>
          <div className="d-flex align-items-center justify-content-around">
            <div className={`${styles["permission__nav-item--selected"]} mr-2`}>
              Assigned Clients
            </div>
            <div className={`${styles["permission__client-count"]} mr-2`}>
              45
            </div>
            <MoreActionsDropdown actions={getDropdownActions()} member={null} />
            <div
              className={`${styles["permission__input-group-search"]} mr-1 input-group-text bg-white border-end-0`}
            >
              <MagnifyingGlass />
              <input
                type="search"
                title="Search Members"
                onChange={(e) => debouncedFilter(e.target.value)}
                className={styles["permission__search-input"]}
                placeholder="Search members..."
              />
            </div>
            <Button
              className={styles["permission__create-new-button"]}
              onClick={() => {
                console.log("Create something new");
              }}
            >
              <OutlinePlus16 className="mr-1" />
              Assign
            </Button>
          </div>
          <div className={styles["permission__permissions-chart"]}>
            <Table>
              <thead className={styles["permission__table-header"]}>
                <tr>
                  <th>
                    <Form.Check
                      type="checkbox"
                      id="select-all"
                      label=""
                      title="Select All"
                      className="text-primary"
                      checked={selectedClients.length === client_chart.length}
                      onChange={() =>
                        handleAllClientSelect(!selectedClients.length)
                      }
                    />
                  </th>
                  <th>View Access</th>
                  <th>Edit Access</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => {
                  return (
                    <tr key={client.id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          id={`select-${client.id}`}
                          label=""
                          title={`Select ${client.id}`}
                          className="text-primary"
                          checked={selectedClients.includes(client.id)}
                          onChange={() => handleClientSelect(client.id)}
                        />
                      </td>
                      <td>{client.name}</td>
                      <td>{client.dob}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Permissions;
